import { create } from 'zustand';

/**
 * svgConvertStore — SVG 轉檔確認視窗的佇列(R415)
 * =============================================================================
 * 入口散在全站(畫布素材格 / 品牌 Logo / 頭像 / 封面 / 素材庫匯入⋯),視窗只掛一份在
 * [providers](../../../app/providers.tsx)(`SvgConvertHost`)—— 跟回報對話框同一個理由:
 * 中間需要一個不看 React tree 位置的開關。
 *
 * 呼叫端拿到的是一個 Promise:使用者按「使用這張」→ resolve 轉好的 `File`;取消 / 關窗
 * → resolve `null`(**不 reject** —— 取消是正常選擇,不是錯誤,呼叫端跳過這個檔就好)。
 *
 * 一次可能來好幾張(多選上傳),所以是佇列:視窗一次只處理最前面那一筆。
 *
 * ⚠️ 刻意不 persist:Promise 跟著頁面活著,存起來沒有意義(也就不必進鑰匙總表)。
 */

export interface SvgConvertTarget {
  /** 目的地收不收透明背景。只有 Logo(品牌規範 Logo / 畫布 Logo 格)為 true。 */
  allowTransparent: boolean;
  /** 轉出來的檔案上限(各入口自己的上傳上限:Logo 5MB / 一般 10MB)。 */
  maxBytes: number;
}

export interface SvgConvertRequest {
  id: number;
  file: File;
  target: SvgConvertTarget;
  /** 同一批的第幾張 / 共幾張 SVG(1 張時不顯示計數)。 */
  index: number;
  total: number;
  resolve: (file: File | null) => void;
}

interface SvgConvertState {
  queue: SvgConvertRequest[];
  /** 結束最前面那一筆(轉好的檔 / null = 取消),換下一筆。 */
  settle: (id: number, file: File | null) => void;
}

let nextId = 1;

export const useSvgConvertStore = create<SvgConvertState>()((set, get) => ({
  queue: [],
  settle: (id, file) => {
    const req = get().queue.find((r) => r.id === id);
    if (!req) return;
    set((s) => ({ queue: s.queue.filter((r) => r.id !== id) }));
    req.resolve(file);
  },
}));

/** 排一筆轉檔確認;resolve = 使用者確認後的檔案,`null` = 取消。 */
export function requestSvgConversion(
  file: File,
  target: SvgConvertTarget,
  position: { index: number; total: number } = { index: 1, total: 1 },
): Promise<File | null> {
  return new Promise((resolve) => {
    const req: SvgConvertRequest = { id: nextId++, file, target, ...position, resolve };
    useSvgConvertStore.setState((s) => ({ queue: [...s.queue, req] }));
  });
}
